import React, { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { 
  onAuthStateChanged, 
  signInWithEmailLink, 
  sendSignInLinkToEmail,
  signOut,
  isSignInWithEmailLink
} from 'firebase/auth'
import type { User } from 'firebase/auth'
import { auth } from '../lib/firebase'

interface AuthContextType {
  user: User | null
  loading: boolean
  sendMagicLink: (email: string) => Promise<void>
  signInWithMagicLink: (email: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user)
      setLoading(false)
      setInitialized(true)
    })

    return () => unsubscribe()
  }, [])

  const sendMagicLink = async (email: string) => {
    const actionCodeSettings = {
      url: `${window.location.origin}/login`,
      handleCodeInApp: true,
    }

    await sendSignInLinkToEmail(auth, email, actionCodeSettings)
    
    // Store the email in localStorage for the sign-in process
    localStorage.setItem('emailForSignIn', email)
  }

  const signInWithMagicLink = async (email: string) => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      try {
        await signInWithEmailLink(auth, email, window.location.href)
        localStorage.removeItem('emailForSignIn')
      } catch (error) {
        console.error('Error signing in with magic link:', error)
        throw error
      }
    }
  }

  const logout = async () => {
    await signOut(auth)
  }

  const value: AuthContextType = {
    user,
    loading: loading || !initialized,
    sendMagicLink,
    signInWithMagicLink,
    logout
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
